import { applyI18n, t } from '../i18n';
import { apiErrorMessage } from '../i18n/apiErrorMessage';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import { dialogHelpTopic } from '../help/dialogHelpTopics';
import { bindModalDismiss, createModalShell, refreshModalTitle } from './modalDialogShell';

export interface InputDialogOptions {
  titleKey: string;
  labelKey: string;
  confirmLabelKey?: string;
  cancelLabelKey?: string;
  defaultValue?: string;
  maxLength?: number;
  required?: boolean;
  /** Use a tall textarea instead of a single-line input. */
  multiline?: boolean;
  rows?: number;
  validate?: (value: string) => string | null;
  submit?: (value: string) => Promise<void>;
  helpTopic?: string;
}

export function invalidSharedFolderEntryName(name: string): string | null {
  if (name.includes('/') || name.includes('\\')) {
    return t('files.owner.invalidName');
  }
  return null;
}

export function throwSharedFolderInputError(cause: unknown): never {
  const code = cause instanceof Error ? cause.message : '';
  if (code === 'invalid_file_name' || code === 'invalid_folder_name') {
    throw new Error(t('files.owner.invalidName'));
  }
  if (code === 'name_already_in_use') {
    throw new Error(t('files.owner.nameInUse'));
  }
  if (code === 'empty_file_name') {
    throw new Error(t('files.owner.invalidName'));
  }
  if (code === 'file_deleted') {
    throw new Error(t('files.owner.editorDeleted'));
  }
  throw new Error(apiErrorMessage(cause));
}

export function showInputDialog(options: InputDialogOptions): Promise<string | null> {
  return new Promise((resolve) => {
    const multiline = options.multiline === true;
    const shell = createModalShell({
      titleKey: options.titleKey,
      titleId: 'input-dialog-title',
      helpTopic: options.helpTopic ?? dialogHelpTopic('input'),
      className: multiline ? 'input-dialog input-dialog--multiline' : 'input-dialog',
    });

    const form = document.createElement('form');
    form.className = 'form-grid';
    form.id = 'input-dialog-form';

    const labelWrap = document.createElement('label');
    const label = document.createElement('span');
    label.id = 'input-dialog-label';
    const input: HTMLInputElement | HTMLTextAreaElement = multiline
      ? document.createElement('textarea')
      : document.createElement('input');
    if (!multiline) {
      (input as HTMLInputElement).type = 'text';
      (input as HTMLInputElement).autocomplete = 'off';
    } else {
      const textarea = input as HTMLTextAreaElement;
      textarea.className = 'message-input';
      textarea.rows = options.rows ?? 8;
    }
    input.name = 'value';
    input.setAttribute('aria-describedby', 'input-dialog-error');
    labelWrap.append(label, input);

    const errorEl = document.createElement('p');
    errorEl.className = 'error-text';
    errorEl.id = 'input-dialog-error';
    errorEl.hidden = true;

    form.append(labelWrap, errorEl);
    shell.body.append(form);

    const cancelButton = document.createElement('button');
    cancelButton.type = 'button';
    cancelButton.className = 'button ghost';
    cancelButton.id = 'input-dialog-cancel';
    cancelButton.innerHTML = `<span data-i18n="${options.cancelLabelKey ?? 'common.cancel'}"></span>`;

    const confirmButton = document.createElement('button');
    confirmButton.type = 'submit';
    confirmButton.className = 'button primary';
    confirmButton.id = 'input-dialog-confirm';
    confirmButton.setAttribute('form', 'input-dialog-form');
    confirmButton.innerHTML = `<span data-i18n="${options.confirmLabelKey ?? 'common.confirm'}"></span>`;

    shell.actions.append(cancelButton, confirmButton);

    input.value = options.defaultValue ?? '';
    if (options.maxLength) {
      input.maxLength = options.maxLength;
    }
    input.required = options.required ?? true;

    const refreshLocale = () => {
      refreshModalTitle(shell, { titleKey: options.titleKey });
      label.textContent = t(options.labelKey);
      applyI18n(shell.dialog);
    };

    refreshLocale();
    const unbindLocale = bindLocaleRefresh(refreshLocale);

    document.body.append(shell.backdrop);

    input.addEventListener('input', () => {
      if (!errorEl.hidden) {
        setError(null);
      }
    });

    let submitting = false;

    const setError = (message: string | null) => {
      if (!message) {
        errorEl.hidden = true;
        errorEl.textContent = '';
        input.removeAttribute('aria-invalid');
        return;
      }
      errorEl.hidden = false;
      errorEl.textContent = message;
      input.setAttribute('aria-invalid', 'true');
    };

    const finish = (value: string | null) => {
      if (submitting) {
        return;
      }
      unbindLocale();
      unbindDismiss();
      shell.backdrop.remove();
      resolve(value);
    };

    const unbindDismiss = bindModalDismiss({
      backdrop: shell.backdrop,
      closeButton: shell.closeButton,
      onClose: () => finish(null),
    });

    cancelButton.addEventListener('click', () => finish(null));

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (submitting) {
        return;
      }

      const value = input.value.trim();
      if (!value && (options.required ?? true)) {
        return;
      }

      const validationError = options.validate?.(value) ?? null;
      if (validationError) {
        setError(validationError);
        input.focus();
        input.select();
        return;
      }

      if (!options.submit) {
        finish(value);
        return;
      }

      submitting = true;
      setError(null);
      cancelButton.disabled = true;
      confirmButton.disabled = true;
      void (async () => {
        try {
          await options.submit?.(value);
          submitting = false;
          finish(value);
        } catch (cause) {
          submitting = false;
          cancelButton.disabled = false;
          confirmButton.disabled = false;
          const message = cause instanceof Error && cause.message.trim()
            ? cause.message
            : t('status.error');
          setError(message);
          input.focus();
          input.select();
        }
      })();
    });

    input.focus();
    if (!multiline) {
      input.select();
    }
  });
}
